/**
 * MigrationScreen — /migrate
 * Landing screen for bringing an already-funded / in-progress project into
 * DrawReady. Shows the migration steps (hero bubble), the fastest migration
 * methods, and a "Book a migration call" option.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRightLeft, ArrowLeft, Table2, ScanLine, Plus, ChevronRight, Phone,
  X, CheckCircle2, Loader2, PhoneCall,
} from 'lucide-react';
import { apiService } from '@/lib/api';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

const STEPS = [
  'Upload a spreadsheet or drop in your invoices & receipts',
  'We map your contractors, budget lines & payments-to-date',
  'Review & confirm what we pulled in',
  "You're caught up to today — track draws going forward",
];

function BookCallModal({ user, onClose }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    preferred_time: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please add your name and email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      await apiService.requestMigrationCall(user.id, form);
      setDone(true);
      toast.success("Request sent — we'll reach out to schedule your migration call");
    } catch (e) {
      toast.error('Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="book-call-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="book-call-modal">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <PhoneCall className="w-5 h-5" style={{ color: GREEN }} />
          <h3 className="text-base font-bold text-slate-900 flex-1">Book a migration call</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: GREEN }} />
            <p className="font-bold text-slate-900">You&apos;re all set</p>
            <p className="text-sm text-slate-500 mt-1">Our team will email you to lock in a time and walk your project into DrawReady together.</p>
            <button onClick={onClose} className="mt-5 text-sm font-bold text-white px-5 py-2.5 rounded-xl" style={{ background: NAVY }} data-testid="book-call-done-btn">Done</button>
          </div>
        ) : (
          <div className="p-6 space-y-3">
            <p className="text-sm text-slate-600">Tell us how to reach you and we&apos;ll set up a guided migration call.</p>
            <div>
              <label className="text-xs font-semibold text-slate-500">Name</label>
              <input value={form.name} onChange={upd('name')} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="book-call-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Email</label>
              <input value={form.email} onChange={upd('email')} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="book-call-email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Phone (optional)</label>
              <input value={form.phone} onChange={upd('phone')} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="book-call-phone" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Best time to reach you (optional)</label>
              <input value={form.preferred_time} onChange={upd('preferred_time')} placeholder="e.g. Weekday afternoons" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="book-call-time" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Anything we should know? (optional)</label>
              <textarea value={form.notes} onChange={upd('notes')} rows={2} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="book-call-notes" />
            </div>
            <button onClick={submit} disabled={submitting} className="w-full mt-2 inline-flex items-center justify-center gap-2 text-sm font-bold text-white px-5 py-3 rounded-xl disabled:opacity-60" style={{ background: GREEN }} data-testid="book-call-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
              {submitting ? 'Sending…' : 'Request my call'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MigrationScreen({ user }) {
  const navigate = useNavigate();
  const [showCall, setShowCall] = useState(false);

  const methods = [
    { icon: Table2, t: 'Upload a spreadsheet', s: 'Contractors, budget lines or payments-to-date. We map the columns.', to: '/dashboard-classic?import=1', testid: 'migrate-upload' },
    { icon: ScanLine, t: 'Drop in invoices & receipts', s: 'AI reads vendor, amount, category & date, files each to a phase.', to: '/dashboard-classic?import=1', testid: 'migrate-invoices' },
    { icon: Plus, t: 'Enter it manually', s: 'Add the project, contractors & paid amounts by hand.', to: '/dashboard-classic?new=1', testid: 'migrate-manual' },
  ];

  return (
    <div className="min-h-screen bg-slate-100" data-testid="migration-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="migrate-back">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        {/* Hero bubble — migration steps */}
        <div className="rounded-3xl p-7 sm:p-9 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, #12508f)` }} data-testid="migration-hero">
          <div className="absolute -right-8 -top-10 w-44 h-44 rounded-full opacity-20" style={{ background: GREEN }} />
          <div className="relative">
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#a8d977' }}>Migrate to DrawReady</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold mt-1 flex items-center gap-2">
              <ArrowRightLeft className="w-7 h-7" /> Bring your project current
            </h1>
            <p className="text-blue-100 mt-2 text-sm max-w-xl">Already funded and underway? Here&apos;s how we catch your project up to today:</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
              {STEPS.map((s, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/10 border border-white/15 rounded-xl px-4 py-3" data-testid={`migration-step-${i + 1}`}>
                  <span className="w-6 h-6 rounded-full bg-white text-xs font-extrabold flex items-center justify-center flex-shrink-0" style={{ color: NAVY }}>{i + 1}</span>
                  <span className="text-sm text-blue-50 leading-snug">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Migration methods */}
        <div className="space-y-3">
          <p className="text-sm font-bold text-slate-700">Pick the fastest way to migrate</p>
          {methods.map((m) => (
            <button
              key={m.testid}
              onClick={() => navigate(m.to)}
              data-testid={m.testid}
              className="w-full text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-sm transition flex items-start gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0"><m.icon className="w-5 h-5" /></div>
              <div>
                <p className="text-sm font-bold text-slate-900">{m.t}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{m.s}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 ml-auto self-center" />
            </button>
          ))}
        </div>

        {/* Book a call */}
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="migration-book-call">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><PhoneCall className="w-5 h-5" style={{ color: GREEN }} /></div>
            <div>
              <p className="text-sm font-bold text-slate-900">Prefer a hand? Book a migration call</p>
              <p className="text-xs text-slate-500">We&apos;ll walk your project into DrawReady with you, live.</p>
            </div>
          </div>
          <button onClick={() => setShowCall(true)} className="inline-flex items-center gap-2 text-sm font-bold text-white px-5 py-2.5 rounded-xl" style={{ background: NAVY }} data-testid="migration-book-call-btn">
            <Phone className="w-4 h-4" /> Book a call
          </button>
        </div>
      </div>

      {showCall && <BookCallModal user={user} onClose={() => setShowCall(false)} />}
    </div>
  );
}
